import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { LOGO_URL, scrollToId } from "@/App";
import { Seal } from "@/components/Seal";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[100svh] items-center overflow-hidden px-6 pb-24 pt-28 sm:px-12 lg:px-20 lg:pt-32"
      data-testid="hero-section"
    >
      <span aria-hidden="true" className="absolute left-6 top-28 h-px w-10 bg-[#9A7B3F]/60 sm:left-12" />
      <span aria-hidden="true" className="absolute left-6 top-28 h-10 w-px bg-[#9A7B3F]/60 sm:left-12" />

      <div className="mx-auto grid w-full max-w-7xl items-center gap-14 md:grid-cols-12 lg:gap-10">
        <motion.div style={{ y: textY }} className="relative z-20 md:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-code text-[11px] uppercase tracking-[0.4em] text-[#9A7B3F]"
            data-testid="hero-edition-tag"
          >
            [ Prelaunch — Public Drop 04 · 2027 ]
          </motion.p>

          <h1
            className="mt-8 font-display text-[16.5vw] uppercase leading-[0.9] tracking-tighter text-[#221812] sm:text-7xl md:relative md:z-20 md:w-[125%] lg:text-[6.5rem] xl:text-[8rem]"
            data-testid="hero-headline"
          >
            <MaskedLine delay={0.35}>The First</MaskedLine>
            <MaskedLine delay={0.5}>
              Wolf<span className="text-[#5E1A22]">.</span>
            </MaskedLine>
          </h1>

          <MaskedLine delay={0.7} className="mt-8">
            <p className="font-display text-xl italic text-[#5E1A22] sm:text-2xl" data-testid="hero-tagline">
              Wear the art. Lead the pack.
            </p>
          </MaskedLine>
          <MaskedLine delay={0.85} className="mt-5">
            <p className="max-w-md text-sm leading-relaxed text-[#55504A] sm:text-base" data-testid="hero-subhead">
              SANZONE street apparel and couture. Public launch: April 2027.
            </p>
          </MaskedLine>
          <MaskedLine delay={1} className="mt-6">
            <p className="font-code text-[11px] uppercase tracking-[0.4em] text-[#5E1A22]" data-testid="hero-instinct-tagline">
              Instinct. Refined.
            </p>
          </MaskedLine>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex max-w-md flex-col gap-4 sm:max-w-none sm:flex-row"
          >
            <button
              onClick={() => scrollToId("#waitlist")}
              className="bg-[#221812] px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#F4EEE3] transition-colors duration-300 hover:bg-[#5E1A22] focus:outline-none focus:ring-2 focus:ring-[#9A7B3F]"
              data-testid="hero-join-waitlist-btn"
            >
              Join the Free Waitlist
            </button>
            <button
              onClick={() => scrollToId("#apply")}
              className="border border-[#221812]/40 px-8 py-4 text-xs uppercase tracking-[0.25em] text-[#221812] transition-colors duration-300 hover:border-[#5E1A22] hover:text-[#5E1A22] focus:outline-none focus:ring-2 focus:ring-[#9A7B3F]"
              data-testid="hero-apply-custom-btn"
            >
              Apply for a Custom Piece
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: logoY }} className="relative md:col-span-5">
          <motion.figure
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-[340px] sm:max-w-[420px]"
          >
            <div aria-hidden="true" className="absolute -inset-3 border border-[#9A7B3F]/40 sm:-inset-5" />
            <div aria-hidden="true" className="absolute -inset-3 translate-x-2.5 translate-y-2.5 border border-[#9A7B3F]/15 sm:-inset-5" />
            <img
              src={LOGO_URL}
              alt="SANZONE logo — SZ monogram with brass wolf eyes above vintage serif SANZONE lettering and the words Instinct. Refined."
              className="relative w-full mix-blend-multiply"
              data-testid="hero-logo"
            />
            <figcaption className="font-code mt-8 text-right text-[10px] uppercase tracking-[0.35em] text-[#8C857B]">
              Fig. 01 — The Mark
            </figcaption>
            <Seal className="absolute -right-3 -top-3 sm:-right-5 sm:-top-5" />
          </motion.figure>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 1 }}
        className="absolute bottom-8 left-6 right-6 flex items-center justify-between sm:left-12 sm:right-12 lg:left-20 lg:right-20"
      >
        <p className="font-code text-[10px] uppercase tracking-[0.35em] text-[#8C857B]">SANZONE Atelier</p>
        <p className="font-code text-[10px] uppercase tracking-[0.35em] text-[#8C857B]" data-testid="hero-launch-badge">
          Public Launch — April 2027
        </p>
      </motion.div>
    </section>
  );
}
